<template>
    <Dropdown
        :disabled="disabled"
        :reset-menu="resetMenu"
        ref="dropdown"
        class="inline-block font-normal"
        @field-disabled="$emit('field-disabled')"
    >
        <DropdownTrigger
            slot-scope="{ toggle, active }"
            :handle-click="toggle"
            :active="active"
            class="form-input form-select hover:bg-40 active:bg-50"
        >
            <div :class="{ 'text-60': empty }" v-html="handle"></div>
        </DropdownTrigger>

        <DropdownMenu slot="menu" class="w-full min-w-dropdown-menu"
                      :class="{ 'top-position': dropdownPosition === 'top' }" slot-scope="{ close }">
            <div v-if="shouldShowFilter" class="relative border-b border-50">
                <icon type="search" class="absolute search-icon-center ml-3 text-70" />
                <input
                    class="search w-full h-9 pl-search outline-none"
                    type="text"
                    v-focus
                    :value="filter"
                    @keyup="updateFilter"
                    @keydown.enter.prevent=""
                    :placeholder="filterPlaceholder"
                />
            </div>
            <div class="wrapper overflow-y-auto" style="max-height: 300px;">
                <div class="group" v-for="(options, groupTitle) in groupedOptions" :key="groupTitle">
                    <h6 class="px-3 py-3" v-if="notEmptyGroup(groupTitle)">{{ groupTitle }}</h6>

                    <ul :class="{'mb-3': hasMultipleGroups}">
                        <li
                            class="px-3 py-3 flex cursor-pointer hover:bg-40 active:bg-50"
                            v-for="(option, index) in options"
                            :key="index"
                            @click.prevent="toggleOption(option)"
                            :class="{ disabled: option.disabled }"
                        >
                            <checkbox
                                v-if="multiple"
                                :checked="isSelected(option)"
                                class="mr-2"
                                :disabled="option.disabled"
                                :class="{ disabled: option.disabled }"
                            />

                            <div
                                class="flex w-full"
                                :class="{
                                        selected: isSelected(option),
                                    }"
                            >
                                <!-- eslint-disable-next-line vue/no-v-html -->
                                <span v-html="option.label"></span>
                                <div
                                    v-if="option.tooltip"
                                    class="self-start ml-1 -mt-1"
                                    v-tooltip="{ content: option.tooltip, boundariesElement: 'body'}"
                                >
                                    <InfoCircleOutlineIcon />
                                </div>
                            </div>
                        </li>
                    </ul>
                </div>
            </div>
            <div
                v-if="multiple"
                class="p-2 border-t border-50 bg-20">
                <button
                    class="btn btn-default btn-primary"
                    type="button"
                    @click="close"
                >
                    {{ $t('Done') }}
                </button>
            </div>
        </DropdownMenu>
    </Dropdown>
</template>

<script>
    import Dropdown from '@/components/Dropdown.vue';
    import DropdownMenu from '@/components/DropdownMenu.vue';
    import DropdownTrigger from '@/components/DropdownTrigger.vue';
    import InfoCircleOutlineIcon from '@/components/Icons/InfoCircleOutlineIcon.vue';

    const EMPTY_GROUP = 'empty_group';

    export default {

        components: {
            Dropdown,
            DropdownMenu,
            DropdownTrigger,
            InfoCircleOutlineIcon,
        },

        props: {
            options: {
                type: Array,
                required: true,
            },
            value: {
                value: {
                    type: [String, Array],
                    default: null,
                },
            },
            disabled: {
                type: Boolean,
                default: false,
            },
            filterable: {
                type: Boolean,
                default: null,
            },
            multiple: {
                type: Boolean,
                default: false,
            },
            placeholder: {
                type: String,
                default: '',
            },
            filterPlaceholder: {
                type: String,
                default() { return this.$t('Search items'); },
            },
            notSelectedLabel: {
                type: String,
                default: '',
            },
            htmlLabels: {
                type: Boolean,
                default: false,
            },
            dropdownPosition: {
                type: String,
                default: 'down',
            },
        },

        data: () => ({
            filter: '',
        }),

        created() {
            if (this.multiple && this.value === null) {
                this.value = [];
            }
        },

        watch: {},

        computed: {

            valuePlaceholder() {
                if (this.placeholder) {
                    return this.placeholder;
                }
                if (this.multiple) {
                    return this.$t('Select items');
                }
                return this.$t('Select item');
            },
            empty() {
                if (this.multiple && this.value.length > 0) {
                    return false;
                }
                if (!this.multiple) {
                    const selectedItem = this.extendedOptions.find((option) => option.value === this.value);

                    if (selectedItem) {
                        return false;
                    }
                    return true;
                }
                return true;
            },
            handle() {
                const separator = this.htmlLabels ? ' ' : ', ';
                if (this.multiple && this.value.length > 0) {
                    return this.extendedOptions
                        .filter((option) => this.value.includes(option.value))
                        .map((option) => option.label)
                        .join(separator);
                }
                if (!this.multiple) {
                    const selectedItem = this.extendedOptions.find((option) => option.value === this.value);

                    if (selectedItem) {
                        return selectedItem.selectedLabel || selectedItem.label;
                    }
                    return this.valuePlaceholder;
                }
                return this.valuePlaceholder;
            },
            shouldShowFilter() {
                return this.filterable === true || (this.filterable === null && this.extendedOptions.length > 10);
            },
            extendedOptions() {
                const options = [...this.options];
                if (this.notSelectedLabel) {
                    options.unshift({
                        label: this.notSelectedLabel,
                        disabled: true,
                        value: null,
                    });
                }
                return options;
            },
            groupedOptions() {
                return this.filteredOptions.reduce((acc, value) => {
                    const group = value.group || EMPTY_GROUP;
                    const groupItems = acc[group] || [];
                    return {
                        ...acc,
                        [group]: [...groupItems, value],
                    };
                }, {});
            },
            filteredOptions() {
                const options = [...this.extendedOptions];
                if (this.filter) {
                    const value = event.target.value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                    return this.extendedOptions.filter((item) => item.label.search(new RegExp(value, 'i')) !== -1);
                }
                return options;
            },
            hasMultipleGroups() {
                return Object.keys(this.groupedOptions).length > 1;
            },
        },
        methods: {
            notEmptyGroup(group) {
                return group !== EMPTY_GROUP;
            },
            isSelected(option) {
                if (this.multiple) {
                    return this.value.includes(option.value);
                }
                return this.value === option.value;
            },
            updateFilter(event) {
                this.filter = event.target.value;
            },
            toggleOption(option) {
                if (option.disabled) {
                    return;
                }
                let newValue;
                if (this.multiple) {
                    const pos = this.value.indexOf(option.value);
                    newValue = [...this.value];
                    if (pos > -1) {
                        newValue.splice(pos, 1);
                    } else {
                        newValue.push(option.value);
                    }
                } else if (this.value !== option.value) {
                    newValue = option.value;
                }

                if (newValue !== undefined) {
                    this.$emit('input', newValue);
                }

                if (!this.multiple) {
                    this.$refs.dropdown.close();
                }
            },
            resetMenu() {
                this.filter = '';
            },
        },
    };
</script>

<style scoped>
    option {
        @apply text-80;
    }

    .top-position {
        bottom: 36px;
    }
</style>
